import { FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { Fragment, memo, useCallback } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import Italian from '../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import Foreign from '../../shared/foreign';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';

interface PlaceProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  isOnlyItalian?: boolean;
  isWithoutAddress?: boolean;
}

const PlaceComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  isOnlyItalian = false,
  isWithoutAddress = false,
}: PlaceProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsItalianChange = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChanges({
        ...(values || {}),
        foreign: {
          ...(values || {}).foreign,
          [key]: value,
        },
      });
    },
    [values, onChanges]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChanges({
        ...(values || {}),
        foreign: {
          ...(valuesNew || {}),
        },
      });
    },
    [values, onChange]
  );

  return (
    <Grid container spacing={2} item>
      {isOnlyItalian ? undefined : (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Luogo celebrazione"
              id="isForeign"
              value={values?.isForeign || 'false'}
              onChange={() => {
                onChanges({
                  isForeign: !values?.isForeign,
                  italian: {
                    location: '',
                    province: '',
                    municipality: null,
                  },
                  foreign: {
                    state: null,
                    province: '',
                    municipality: null,
                  },
                });
              }}
            >
              <FormControlLabel
                value="false"
                control={<Radio />}
                label="Luogo celebrazione Italia"
                disabled={disabled}
              />
              <FormControlLabel
                value="true"
                control={<Radio />}
                label="Luogo celebrazione estero"
                disabled={disabled}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      {values?.isForeign && !isOnlyItalian ? (
        <Foreign
          disabledComponent={disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          labelState="Stato di celebrazione"
          required
        />
      ) : (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onFieldItalianChange}
          onChanges={onFieldsItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          fromDate={values?.dateEvent || DateTime.now()}
          labelMunicipality="Comune di celebrazione"
          labelProvince="Provincia di celebrazione"
          required
        />
      )}
      <Grid item xs={12}>
        <PmiTextField
          label="Luogo di celebrazione *"
          aria-label="Luogo di celebrazione"
          id="place"
          name="place"
          fullWidth
          multiline
          variant="outlined"
          value={values?.place || ''}
          onChange={(event) => {
            onChange('place', event.target.value);
          }}
          disabled={disabled}
          error={touched?.place && Boolean(errors?.place)}
          helperText={errors?.place}
        />
      </Grid>
      {isWithoutAddress ? undefined : (
        <Fragment>
          <Grid item xs={12} md={10}>
            <PmiTextField
              label="Indirizzo di celebrazione *"
              aria-label="Indirizzo di celebrazione"
              id="address"
              name="address"
              fullWidth
              multiline
              variant="outlined"
              value={values?.address || ''}
              onChange={(event) => {
                onChange('address', event.target.value);
              }}
              disabled={disabled}
              error={touched?.address && Boolean(errors?.address)}
              helperText={errors?.address}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              label="Nr Civico *"
              aria-label="Nr Civico"
              id="houseNumber"
              name="houseNumber"
              fullWidth
              multiline
              variant="outlined"
              value={values?.houseNumber || ''}
              onChange={(event) => {
                onChange('houseNumber', event.target.value);
              }}
              disabled={disabled}
              error={touched?.houseNumber && Boolean(errors?.houseNumber)}
              helperText={errors?.houseNumber}
            />
          </Grid>
        </Fragment>
      )}
    </Grid>
  );
};

PlaceComponent.displayName = 'Form Specific Data Civil Union - Place component';

const Place = memo(PlaceComponent);

export default Place;
